import { IonButton, IonIcon, useIonRouter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { chevronBackOutline, shareOutline } from "ionicons/icons";
import { toast } from "sonner";
import { Share } from "@/lib/capacitor/share";
import { logger } from "@/logger";

interface ProjectHeaderProps {
  /** Project name for sharing */
  projectName?: string;
  /** Project ID for deep link */
  projectId: string;
  /** Organization slug for deep link */
  organizationSlug?: string;
  /** Backend URL for deep link */
  backendUrl: string;
  /** URL to navigate back to (defaults to projects list) */
  backUrl?: string;
}

/**
 * Fixed header with frosted glass back and share buttons. Positioned absolutely on top of the hero
 * image.
 */
export function ProjectHeader({
  projectName,
  projectId,
  organizationSlug,
  backendUrl,
  backUrl,
}: ProjectHeaderProps) {
  const { t } = useTranslation();
  const router = useIonRouter();

  const handleShare = async () => {
    try {
      const canShareResult = await Share.canShare();
      if (canShareResult) {
        await Share.share({
          title: projectName || "Project",
          text: `Check out this project: ${projectName}`,
          url: `${backendUrl}/deeplink/${organizationSlug}/app/projects/${projectId}`,
        });
      } else {
        toast.error(t("shift:transfer.share-unsupported"));
      }
    } catch (err) {
      logger.error("Share failed:", err);
    }
  };

  return (
    <div className="absolute left-0 right-0 top-[36px] flex items-center justify-between px-4 py-4">
      <IonButton
        fill="clear"
        shape="round"
        className="m-0 h-12 w-12 rounded-full p-0"
        style={{
          "--background": "rgba(241, 241, 241, 0.8)",
          "--background-hover": "rgba(241, 241, 241, 0.9)",
          "--background-activated": "rgba(241, 241, 241, 1)",
          "--backdrop-filter": "blur(8px)",
        }}
        onClick={() => {
          // Navigate back and clear the project detail from history
          router.push(backUrl || "/app/projects", "back", "pop");
        }}
      >
        <IonIcon className="text-2xl text-txt-primary" slot="icon-only" icon={chevronBackOutline} />
      </IonButton>
      <IonButton
        fill="clear"
        shape="round"
        className="m-0 h-12 w-12 rounded-full p-0"
        style={{
          "--background": "rgba(241, 241, 241, 0.8)",
          "--background-hover": "rgba(241, 241, 241, 0.9)",
          "--background-activated": "rgba(241, 241, 241, 1)",
          "--backdrop-filter": "blur(8px)",
        }}
        onClick={handleShare}
      >
        <IonIcon className="text-2xl text-txt-primary" slot="icon-only" icon={shareOutline} />
      </IonButton>
    </div>
  );
}
